import TaxHelper from "./TaxHelper";
import * as NumberFormtHelper from "helpers/NumberFormatHelper";
import { PRODUCT_TYPES, ORDER_TYPES, CHARGE_CATEGORIES, TAX_PREFERENCES, KOT_STATUS } from "const";

function calculateSaleItemPayableAndCharges(saleItem, rate, roundedQty, tax, cess, charges, isNCOrder) {
  const appliedCharges = isNCOrder ? [] : TaxHelper.getItemCharges(saleItem, charges);
  let totalCharges = 0;
  let totalDiscounts = 0;

  appliedCharges.forEach((c) => {
    if (c.category === CHARGE_CATEGORIES.DISCOUNT) {
      totalDiscounts += c.value;
    } else {
      totalCharges += c.value;
    }
  });

  const payable = rate * roundedQty + tax + cess + totalCharges - totalDiscounts;
  return { appliedCharges, totalCharges, totalDiscounts, payable };
}

function computeCartItemChargesAndTaxes(cartItem, charges, selectedChargeGroupIds, isNCOrder) {
  const appliedCharges = isNCOrder ? [] : TaxHelper.getItemCharges(cartItem, charges, selectedChargeGroupIds);
  let totalCharges = 0;
  let totalDiscounts = 0;

  appliedCharges.forEach((c) => {
    if (c.category === CHARGE_CATEGORIES.DISCOUNT) {
      totalDiscounts += c.value;
    } else {
      totalCharges += c.value;
    }
  });

  return { appliedCharges, totalCharges, totalDiscounts };
}

function calculateDiscountAmount(
  isExcludingTax,
  updatedRate,
  taxPercentage,
  cessPercentage,
  roundedQty,
  discount,
  cartHelperRef,
) {
  const discountBaseRate = isExcludingTax
    ? updatedRate * (1 + (Number(taxPercentage || 0) + Number(cessPercentage || 0)) / 100)
    : updatedRate;

  return cartHelperRef.getItemDiscountAmount(discountBaseRate, roundedQty, discount);
}

function calculateChargeSummary(cartItem, charges, selectedChargeGroupIds, isNCOrder) {
  return computeCartItemChargesAndTaxes(cartItem, charges, selectedChargeGroupIds, isNCOrder);
}

function calculatePricingSummary(
  isNCOrder,
  cartItem,
  updatedRate,
  taxPercentage,
  cessPercentage,
  discount,
  roundedQty,
  totalCharges,
  totalDiscounts,
  cartHelperRef,
) {
  if (isNCOrder) {
    return {
      itemUnitPrice: TaxHelper.getItemUnitRate(updatedRate, taxPercentage, cessPercentage, discount),
      totalBeforeTax: 0,
      tax: 0,
      cess: 0,
      normal_payable: 0,
      totalPayable: 0,
    };
  }

  const itemUnitPrice = TaxHelper.getItemUnitRate(updatedRate, taxPercentage, cessPercentage, discount);
  const totalBeforeTax = itemUnitPrice * roundedQty;
  const tax = TaxHelper.getItemTax(taxPercentage, totalBeforeTax);
  const cess = TaxHelper.getItemCess(cessPercentage, totalBeforeTax);

  const normal_payable = cartHelperRef.getSaleItemAmountWithDiscountAndTaxes(cartItem, {
    totalBeforeTax,
    tax,
    cess,
  });

  return {
    itemUnitPrice,
    totalBeforeTax,
    tax,
    cess,
    normal_payable,
    totalPayable: normal_payable + totalCharges - totalDiscounts,
  };
}

function calculateCartItemTotals(
  isNCOrder,
  isExcludingTax,
  updatedRate,
  taxPercentage,
  cessPercentage,
  discount,
  roundedQty,
  cartItem,
  charges,
  selectedChargeGroupIds,
  cartHelperRef,
) {
  const discountAmount = isNCOrder
    ? 0
    : calculateDiscountAmount(
        isExcludingTax,
        updatedRate,
        taxPercentage,
        cessPercentage,
        roundedQty,
        discount,
        cartHelperRef,
      );

  const { appliedCharges, totalCharges, totalDiscounts } = calculateChargeSummary(
    cartItem,
    charges,
    selectedChargeGroupIds,
    isNCOrder,
  );

  return {
    discountAmount,
    appliedCharges,
    totalCharges,
    totalDiscounts,
    ...calculatePricingSummary(
      isNCOrder,
      cartItem,
      updatedRate,
      taxPercentage,
      cessPercentage,
      discount,
      roundedQty,
      totalCharges,
      totalDiscounts,
      cartHelperRef,
    ),
  };
}

class CartHelper {
  getSaleItemAmount = (saleItem) => {
    return NumberFormtHelper.amountExtendedRoundOff(saleItem.qty * saleItem.rate);
  };

  getSaleItemAmountWithDiscount = (saleItem) => {
    let amount = this.getSaleItemAmount(saleItem);
    if (saleItem.discountPercentage) {
      const discount = (saleItem.discountPercentage * amount) / 100;
      amount -= discount;
    }
    return NumberFormtHelper.amountExtendedRoundOff(amount);
  };

  getSaleItemAmountWithDiscountAndTaxes = (saleItem, { totalBeforeTax = null, cess = null, tax = null } = {}) => {
    const amount = totalBeforeTax ?? this.getSaleItemAmountWithDiscount(saleItem);
    const cessAmount = cess ?? TaxHelper.getItemCess(saleItem, amount);
    const taxAmount = tax ?? TaxHelper.getItemTax(saleItem, amount);
    return NumberFormtHelper.amountExtendedRoundOff(amount + taxAmount + cessAmount);
  };

  // getSaleItemAmountWithDiscountAndTaxesUsingCartItem = (cartItem) => {
  //   let amount = this.getSaleItemAmountWithDiscount(cartItem);
  //   amount += cartItem.cess;
  //   amount += cartItem.tax;
  //   return NumberFormtHelper.amountExtendedRoundOff(amount);
  // };

  // getTotalWithDiscountAndTaxes = (cartItems) => {
  //   return (cartItems || [])
  //     .map((cartItem) => this.getSaleItemAmountWithDiscountAndTaxesUsingCartItem(cartItem))
  //     .reduce((acc, amount) => acc + amount, 0);
  // };

  getTotalPayable = (cartItems) => {
    return NumberFormtHelper.amountRoundOff((cartItems || []).reduce((acc, cartItem) => acc + cartItem.payable, 0));
  };

  getTotalNormalPayable = (cartItems) => {
    return NumberFormtHelper.amountRoundOff(
      (cartItems || []).reduce((acc, cartItem) => acc + (cartItem.normal_payable || 0), 0),
    );
  };

  getItemDiscountAmount = (rate = 0, qty = 0, discountPercentage = 0) => {
    const totalAmount = rate * qty;
    const discount = (totalAmount * discountPercentage) / 100;
    return discount;
  };

  getTotalAmount = (cartItems) => {
    return NumberFormtHelper.amountRoundOff(
      (cartItems || []).reduce((acc, cartItem) => acc + cartItem.rate * cartItem.qty, 0),
    );
  };

  getTotalDiscount = (cartItems) => {
    return NumberFormtHelper.amountRoundOff(cartItems.reduce((acc, cartItem) => acc + cartItem.discount, 0));
  };

  getTotalCessAmount = (cartItems) => {
    return NumberFormtHelper.amountRoundOff((cartItems || []).reduce((acc, cartItem) => acc + cartItem.cess, 0));
  };

  getTotalTaxAmount = (cartItems) => {
    return NumberFormtHelper.amountRoundOff((cartItems || []).reduce((acc, cartItem) => acc + cartItem.tax, 0));
  };

  getUpdadingItem = (items, cartItem) => {
    return items.find((i) => i.id === cartItem.id);
  };

  /** @deprecated Use `SalesHelper.calculateSaleItemSummary` instead. */
  getSaleItemData = (activeSale, saleItem, charges = []) => {
    const isNCOrder = activeSale?.order_type === ORDER_TYPES.NC;
    const taxPreference = TaxHelper.getTaxPreference();
    const isExcludingTax = taxPreference === TAX_PREFERENCES.EXCLUDING_TAX;

    const inputRate = TaxHelper.getSaleItemBaseRate(saleItem);
    const rate = TaxHelper.getItemUnitRate(
      inputRate,
      saleItem.taxPercentage,
      saleItem.cessPercentage,
      saleItem.discountPercentage,
    );
    let discountBaseRate = inputRate;
    if (isExcludingTax) {
      discountBaseRate =
        inputRate * (1 + (Number(saleItem.taxPercentage || 0) + Number(saleItem.cessPercentage || 0)) / 100);
    }
    const discount = isNCOrder
      ? 0
      : this.getItemDiscountAmount(discountBaseRate, saleItem.qty, saleItem.discountPercentage);
    const roundedQty = NumberFormtHelper.quantityRoundOff(saleItem.qty);
    // Taxable amount before taxes: rate (tax-exclusive) * quantity
    const totalBeforeTax = isNCOrder ? 0 : NumberFormtHelper.amountRoundOff(rate * roundedQty);
    const tax = TaxHelper.getItemTax(saleItem.taxPercentage, rate) * roundedQty;
    const cess = TaxHelper.getItemCess(saleItem.cessPercentage, rate) * roundedQty;

    const { appliedCharges, totalCharges, totalDiscounts, payable } = calculateSaleItemPayableAndCharges(
      saleItem,
      rate,
      roundedQty,
      tax,
      cess,
      charges,
      isNCOrder,
    );

    // Undiscounted rate including tax
    const rateAndTax = isExcludingTax
      ? inputRate * (1 + (Number(saleItem.taxPercentage || 0) + Number(saleItem.cessPercentage || 0)) / 100)
      : inputRate;

    return {
      ...saleItem,
      rate: isNCOrder ? 0 : NumberFormtHelper.amountRoundOff(rate),
      rateAndTax: NumberFormtHelper.amountRoundOff(rateAndTax),
      totalBeforeTax,
      normal_payable: isNCOrder ? 0 : NumberFormtHelper.amountRoundOff(payable),
      discount: NumberFormtHelper.amountRoundOff(discount + totalDiscounts),
      tax: isNCOrder ? 0 : tax,
      cess: isNCOrder ? 0 : cess,
      charges: appliedCharges,
      totalCharges,
      totalDiscounts,
      payable: isNCOrder ? 0 : NumberFormtHelper.amountRoundOff(payable),
    };
  };

  getApplicableCategoryPrice = (itemId, qty, priceCategory, priceCategoryItems) => {
    if (!priceCategory || !priceCategory.id) return null;

    const applicablePrices = priceCategoryItems
      .filter((pci) => pci.item === itemId && pci.category?.id === priceCategory.id && qty >= pci.minimum_quantity)
      .sort((a, b) => b.minimum_quantity - a.minimum_quantity); // highest first

    return applicablePrices.length ? applicablePrices[0].price : null;
  };

  getApplicableCustomerPrice = (itemId, qty, customer, priceCategoryItems) => {
    const categoryId = customer?.group?.price_category?.id;
    if (!categoryId) return null;

    const applicablePrices = priceCategoryItems
      .filter((pci) => pci.item === itemId && pci.category?.id === categoryId && qty >= pci.minimum_quantity)
      .sort((a, b) => b.minimum_quantity - a.minimum_quantity); // highest first

    return applicablePrices.length ? applicablePrices[0].price : null;
  };

  getEligibleSaleItemsForAddon(addonMappings, saleItems) {
    return addonMappings
      .map((m) => {
        const saleItem = saleItems.find((si) => si.productId === m.item);
        if (!saleItem) return null;

        return {
          saleItem,
          maxQty: saleItem.qty * m.max_quantity,
          priceDelta: m.price_delta,
        };
      })
      .filter(Boolean);
  }

  getAddonMappingsForItem(itemId, addonMappings) {
    return addonMappings.filter((m) => m.addon_item === itemId && m.is_available);
  }

  isStockAvailableForSale = (product, qty) => {
    if (product.type === PRODUCT_TYPES.SERVICE) return true;
    return product.stock >= qty;
  };

  recomputeAddonMaxQty(saleItem, activeSaleItems, addonMappings) {
    const mappings = addonMappings.filter((m) => m.addon_item === saleItem.productId && m.is_available);

    if (mappings.length === 0) return null;

    return mappings.reduce((sum, m) => {
      const parentSaleItem = activeSaleItems.find((si) => si.productId === m.item);
      if (!parentSaleItem) return sum;

      return sum + parentSaleItem.qty * m.max_quantity;
    }, 0);
  }

  prepareCartItemContext(product, saleItem, qty, discountPercentage, rate, orderType) {
    const isNCOrder = orderType === ORDER_TYPES.NC;

    const cessPercentage = saleItem.cessPercentage ?? product.cessPercentage ?? product.cess;
    const taxPercentage = saleItem.taxPercentage ?? product.taxPercentage ?? product.tax;

    const taxPreference = TaxHelper.getTaxPreference();
    const isExcludingTax = taxPreference === TAX_PREFERENCES.EXCLUDING_TAX;

    const roundedQty = NumberFormtHelper.amountRoundOff(qty);

    saleItem.item = product;
    const updatedRate = rate ?? NumberFormtHelper.amountRoundOff(TaxHelper.getSaleItemBaseRate(saleItem) ?? 0);
    const discount = discountPercentage !== null ? discountPercentage : (saleItem.discountPercentage ?? 0);

    return {
      isNCOrder,
      isExcludingTax,
      roundedQty,
      updatedRate,
      discount,
      taxPercentage,
      cessPercentage,
      cartItem: {
        ...saleItem,
        rate: updatedRate,
        discountPercentage: discount,
        qty: roundedQty,
        item: product,
      },
    };
  }

  buildCartItemData(product, saleItem, rate, context, totals) {
    const { isNCOrder, isExcludingTax, updatedRate, discount, taxPercentage, cessPercentage, cartItem } = context;

    const rateAndTax = isExcludingTax
      ? updatedRate * (1 + (Number(taxPercentage || 0) + Number(cessPercentage || 0)) / 100)
      : updatedRate;

    return {
      ...cartItem,
      id: product.id,
      productId: product.id,
      name: product.name,
      qty: cartItem.qty,
      rate: isNCOrder ? 0 : NumberFormtHelper.amountRoundOff(totals.itemUnitPrice),
      rateAndTax: NumberFormtHelper.amountRoundOff(rateAndTax),
      totalBeforeTax: totals.totalBeforeTax,
      discount: NumberFormtHelper.amountRoundOff(totals.discountAmount + totals.totalDiscounts),
      discountPercentage: discount,
      tax: totals.tax,
      cess: totals.cess,
      charges: totals.appliedCharges,
      totalCharges: totals.totalCharges,
      totalDiscounts: totals.totalDiscounts,
      payable: NumberFormtHelper.amountRoundOff(totals.totalPayable),
      normal_payable: NumberFormtHelper.amountRoundOff(totals.normal_payable),
      taxPercentage,
      cessPercentage,
      hsn: product.hsn,
      status: KOT_STATUS.DRAFT,
    };
  }

  getCartItemData = (
    product,
    saleItem,
    qty,
    {
      discountPercentage = null,
      price: _price = null,
      orderType = null,
      rate = null,
      selectedChargeGroupIds = null,
    } = {},
    charges = [],
  ) => {
    const item = this.prepareCartItemContext(product, saleItem, qty, discountPercentage, rate, orderType);

    const totals = calculateCartItemTotals(
      item.isNCOrder,
      item.isExcludingTax,
      item.updatedRate,
      item.taxPercentage,
      item.cessPercentage,
      item.discount,
      item.roundedQty,
      item.cartItem,
      charges,
      selectedChargeGroupIds,
      this,
    );

    return this.buildCartItemData(product, saleItem, rate, item, totals);
  };
}

const cartHelper = new CartHelper();
export default cartHelper;
